import { FC, useEffect, useRef, useState } from "react";
import styles from './styles.module.scss';

/** false — не показывать «В разработке», «В работе», «Планируется» на карточках */
const SHOW_PROJECT_STATUS_BADGES = false;

/** false — временно скрыть бейдж «i» с поповером возле заголовка проекта */
const SHOW_PROJECT_INFO_BADGE = false;

interface Project {
    id: string;
    title: string;
    category: string;
    description: string;
    tags: string[];
    image: string;
    status?: string;
    info?: string;
    note?: string;
}

/* Статусы в данных ниже не отображаются при SHOW_PROJECT_STATUS_BADGES === false */
const projects: Project[] = [
    {
        id: 'ansarium-platform',
        title: 'Образовательная платформа Ансариум',
        category: 'Платформы',
        description: 'Комплексная LMS-система с личными кабинетами, интерактивными уроками, проверкой домашних заданий, системой оценок и аналитикой прогресса учеников',
        tags: ['React', 'TypeScript', 'Python', 'PostgreSQL', 'WebSocket'],
        image: 'gradient-1',
        status: 'В разработке',
        info: 'Образовательная платформа Ансариум — это комплексная LMS-система, разработанная ООО «Ансариум». Права на платформу в полном объёме принадлежат исключительно ООО «Ансариум». Использование платформы доступно для пользователей на условиях лицензионного соглашения. Конкретные условия лицензионного соглашения, в том числе стоимость лицензии на платформу Ансариум, устанавливаются в зависимости от запросов пользователя в отношении функционала платформы. Минимальная стоимость лицензии — 100 000 руб.',
        note: 'Образовательная платформа Ансариум — это комплексная LMS-система, разработанная ООО «Ансариум». Права на платформу в полном объёме принадлежат исключительно ООО «Ансариум». Использование платформы доступно для пользователей на условиях лицензионного соглашения. Конкретные условия лицензионного соглашения, в том числе стоимость лицензии на платформу Ансариум, устанавливаются в зависимости от запросов пользователя в отношении функционала платформы. Минимальная стоимость лицензии — 100 000 руб.'
    },
    {
        id: 'ai-assistant',
        title: 'AI-ассистент для обучения',
        category: 'Искусственный интеллект',
        description: 'Интеллектуальный помощник на базе ИИ для автоматизации проверки кода, генерации заданий и персонализированных рекомендаций студентам',
        tags: ['Python', 'OpenAI API', 'FastAPI', 'PostgreSQL'],
        image: 'gradient-2',
        status: 'В разработке'
    },
    {
        id: 'automation-tools',
        title: 'Инструменты автоматизации',
        category: 'Автоматизация',
        description: 'Telegram-боты и сервисы для автоматизации приема заявок, рассылок, уведомлений и коммуникации со студентами',
        tags: ['Python', 'Telegram Bot API', 'PostgreSQL', 'Redis'],
        image: 'gradient-3',
        status: 'В работе'
    },
    {
        id: 'admin-panel',
        title: 'Панель управления для преподавателей',
        category: 'Платформы',
        description: 'Административная панель с расширенной аналитикой, управлением курсами, мониторингом студентов и генерацией отчетов',
        tags: ['React', 'TypeScript', 'Python', 'PostgreSQL'],
        image: 'gradient-4',
        status: 'Планируется'
    }
];

const categories = ['Все проекты', 'Платформы', 'Искусственный интеллект', 'Автоматизация'];

export const Projects: FC = () => {
    const [selectedCategory, setSelectedCategory] = useState('Все проекты');
    const [openInfoId, setOpenInfoId] = useState<string | null>(null);
    const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});
    const infoRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!openInfoId) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
                setOpenInfoId(null);
            }
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpenInfoId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [openInfoId]);

    const filteredProjects = selectedCategory === 'Все проекты'
        ? projects
        : projects.filter(p => p.category === selectedCategory);

    return (
        <div className={styles.projects} id="projects">
            <div className={styles.__in}>
                
                <div className={styles.header}>
                    <div className={styles.badge}>
                        Разработка
                    </div>
                    <h2 className={styles.title}>
                        Над чем мы работаем
                    </h2>
                    <p className={styles.subtitle}>
                        Актуальные проекты и направления нашей работы в создании IT-решений
                    </p>
                </div>

                <div className={styles.filters}>
                    {categories.map(category => (
                        <button
                            key={category}
                            className={`${styles.filter_button} ${selectedCategory === category ? styles.active : ''}`}
                            onClick={() => setSelectedCategory(category)}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                <div className={styles.grid}>
                    {filteredProjects.map((project, index) => (
                        <div
                            key={project.id}
                            className={`${styles.card} ${openInfoId === project.id ? styles.card_info_open : ''}`}
                            style={{
                                animationDelay: `${index * 0.1}s`
                            } as React.CSSProperties}
                        >
                            <div className={`${styles.image} ${styles[project.image]}`}>
                                <div className={styles.overlay}>
                                    {SHOW_PROJECT_STATUS_BADGES && project.status ? (
                                        <div className={styles.status_badge}>
                                            {project.status}
                                        </div>
                                    ) : null}
                                    <div className={styles.category_badge}>
                                        {project.category}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.content}>
                                <div className={styles.title_row}>
                                    <h3 className={styles.project_title}>{project.title}</h3>
                                    {SHOW_PROJECT_INFO_BADGE && project.info && (
                                        <div
                                            className={styles.info_wrapper}
                                            ref={openInfoId === project.id ? infoRef : null}
                                        >
                                            <button
                                                type="button"
                                                className={styles.info_button}
                                                aria-label="Подробнее о проекте"
                                                aria-expanded={openInfoId === project.id}
                                                onClick={() =>
                                                    setOpenInfoId(prev =>
                                                        prev === project.id ? null : project.id
                                                    )
                                                }
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                                                    <circle cx="8" cy="4.5" r="0.9" fill="currentColor" />
                                                    <path d="M8 7.25V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                            </button>
                                            <div
                                                className={`${styles.info_popover} ${openInfoId === project.id ? styles.open : ''}`}
                                                role="tooltip"
                                            >
                                                {project.info}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className={styles.description}>{project.description}</p>

                                {project.note && (
                                    <div className={`${styles.note} ${expandedNotes[project.id] ? styles.note_expanded : ''}`}>
                                        <div className={styles.note_text}>
                                            {project.note}
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.note_toggle}
                                            aria-expanded={!!expandedNotes[project.id]}
                                            onClick={() =>
                                                setExpandedNotes(prev => ({
                                                    ...prev,
                                                    [project.id]: !prev[project.id]
                                                }))
                                            }
                                        >
                                            {expandedNotes[project.id] ? 'Свернуть' : 'Показать полностью'}
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                                                <path
                                                    d="M3 4.5L6 7.5L9 4.5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                )}

                                <div className={styles.tags}>
                                    {project.tags.map(tag => (
                                        <span key={tag} className={styles.tag}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                {/* <button className={styles.link_button}>
                                    Подробнее
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path 
                                            d="M6 12L10 8L6 4" 
                                            stroke="currentColor" 
                                            strokeWidth="2" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button> */}
                            </div>
                        </div>
                    ))}
                </div>

                {filteredProjects.length === 0 && (
                    <div className={styles.empty}>
                        <p>Проектов в этой категории пока нет</p>
                    </div>
                )}

            </div>
        </div>
    );
};

